import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../Layout';
import assessmentAPI from '../../services/AssessmentAPI';
import { API_CONFIG } from '../../config/api';

import {
    AlertCircle,
    ArrowLeft,
    BookOpen,
    CheckCircle,
    Clock,
    FileText,
    GraduationCap,
    Loader,
    Save,
    Upload,
    X,
    Eye,
    Edit
} from 'lucide-react';

const StudentMarksEntry = () => {
    const { assessmentId } = useParams();
    const navigate = useNavigate();

    // State management
    const [assessment, setAssessment] = useState(null);
    const [existingMarks, setExistingMarks] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [notification, setNotification] = useState(null);
    const [questionMarks, setQuestionMarks] = useState([]);
    const [selfFeedback, setSelfFeedback] = useState('');
    const [totalMarks, setTotalMarks] = useState(0);
    const [percentage, setPercentage] = useState(0);
    const [grade, setGrade] = useState('');

    useEffect(() => {
        fetchAssessmentDetails();
        fetchExistingMarks();
    }, [assessmentId]);

    useEffect(() => {
        calculateTotals();
    }, [questionMarks]);

    const fetchAssessmentDetails = async () => {
        try {
            setLoading(true);

            // First, try to get from the list (which works)
            const listResponse = await fetch(`${API_CONFIG.BASE_URL}/student/assessments`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
                    'Content-Type': 'application/json'
                }
            });

            if (listResponse.ok) {
                const listData = await listResponse.json();
                const foundAssessment = listData.assessments?.find(a => a.id === assessmentId);

                if (foundAssessment) {
                    setAssessment(foundAssessment);

                    // Initialize question marks array
                    if (foundAssessment.questions) {
                        const initialMarks = foundAssessment.questions.map((question, index) => ({
                            question_id: question.id || `q_${index + 1}`,
                            question_number: index + 1,
                            max_marks: question.marks || 1,
                            obtained_marks: 0,
                            competency_id: question.competency_id || null,
                            competency_name: question.competency_name || null
                        }));
                        setQuestionMarks(initialMarks);
                    }
                    setLoading(false);
                    return;
                }
            }

            // Fallback: If not found in list, show error
            setError("Assessment not found or not assigned to you");
            setLoading(false);
        } catch (err) {
            console.error("Error fetching assessment:", err);
            setError("Failed to load assessment details");
            setLoading(false);
        }
    };

    const fetchExistingMarks = async () => {
        try {
            console.log('Fetching marks for assessment:', assessmentId);
            const response = await assessmentAPI.getMyMarks(assessmentId);
            console.log('Marks response:', response);

            if (response.marks) {
                console.log('Setting existing marks:', response.marks);
                setExistingMarks(response.marks);

                // Populate form with existing marks
                if (response.marks.question_marks && response.marks.question_marks.length > 0) {
                    console.log('Setting question marks:', response.marks.question_marks);
                    setQuestionMarks(response.marks.question_marks);
                } else if (response.marks.total_marks && response.marks.total_marks > 0) {
                    // Teacher entered total marks only - create question marks from total
                    console.log('Teacher entered total marks only:', response.marks.total_marks);
                    // Distribute marks across questions proportionally
                    if (questionMarks.length > 0) {
                        const updatedMarks = questionMarks.map(q => ({
                            ...q,
                            obtained_marks: 0 // Can't distribute without knowing individual scores
                        }));
                        setQuestionMarks(updatedMarks);
                    }
                } else {
                    console.log('No question marks found in response');
                }

                if (response.marks.self_feedback) {
                    setSelfFeedback(response.marks.self_feedback);
                }
            } else {
                console.log('No marks found in response');
            }
        } catch (err) {
            console.error("Error fetching existing marks:", err);
            // Don't show error for this as it's expected for new submissions
        }
    };

    const calculateTotals = () => {
        const total = questionMarks.reduce((sum, mark) => sum + (mark.obtained_marks || 0), 0);
        const maxTotal = questionMarks.reduce((sum, mark) => sum + (mark.max_marks || 0), 0);
        const percent = maxTotal > 0 ? (total / maxTotal) * 100 : 0;
        const calculatedGrade = assessmentAPI.calculateGrade(percent);

        setTotalMarks(total);
        setPercentage(Math.round(percent * 10) / 10); // Round to 1 decimal
        setGrade(calculatedGrade);
    };

    const handleMarkChange = (index, value) => {
        const numValue = parseFloat(value) || 0;
        const maxMark = questionMarks[index].max_marks;

        // Validate mark doesn't exceed maximum
        if (numValue > maxMark) {
            showNotification('error', `Mark cannot exceed ${maxMark} for this question`);
            return;
        }

        if (numValue < 0) {
            showNotification('error', 'Mark cannot be negative');
            return;
        }

        const updatedMarks = [...questionMarks];
        updatedMarks[index] = {
            ...updatedMarks[index],
            obtained_marks: numValue
        };
        setQuestionMarks(updatedMarks);
    };

    const validateSubmission = () => {
        const errors = assessmentAPI.validateMarks(questionMarks, assessment?.max_marks || 0);

        if (errors.length > 0) {
            showNotification('error', errors[0]);
            return false;
        }

        // Check if all questions have marks entered
        const incompleteQuestions = questionMarks.filter(mark =>
            mark.obtained_marks === null || mark.obtained_marks === undefined || mark.obtained_marks === ''
        );

        if (incompleteQuestions.length > 0) {
            showNotification('error', 'Please enter marks for all questions');
            return false;
        }

        return true;
    };

    const handleSubmit = async () => {
        if (!validateSubmission()) return;

        try {
            setSaving(true);

            if (existingMarks && existingMarks.status !== 'approved') {
                // Update existing submission (if not approved)
                await assessmentAPI.updateMyMarks(assessmentId, questionMarks);
                showNotification('success', 'Marks updated successfully!');
            } else if (!existingMarks) {
                // Create new submission
                await assessmentAPI.submitAssessmentMarks(assessmentId, questionMarks);
                showNotification('success', 'Marks submitted successfully!');
            } else {
                // Cannot edit approved marks
                showNotification('error', 'Cannot edit approved marks');
                return;
            }

            // Refresh existing marks
            await fetchExistingMarks();

        } catch (err) {
            console.error("Error submitting marks:", err);
            const errorMessage = err.message || 'Failed to submit marks. Please try again.';
            showNotification('error', errorMessage);
        } finally {
            setSaving(false);
        }
    };

    const showNotification = (type, message) => {
        setNotification({ type, message });
        setTimeout(() => setNotification(null), 5000);
    };

    const goBack = () => {
        navigate('/assessments');
    };

    const canEdit = () => {
        return !existingMarks || existingMarks.status !== 'approved';
    };

    if (loading) {
        return (
            <Layout>
                <div className="bg-white p-8 min-h-screen rounded-b-2xl rounded-tr-2xl shadow-xl font-[poppins]">
                    <div className="flex justify-center items-center h-64">
                        <Loader className="w-8 h-8 text-[#D67C2C] animate-spin" />
                        <span className="ml-3 text-lg text-gray-600">Loading assessment...</span>
                    </div>
                </div>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout>
                <div className="bg-white p-8 min-h-screen rounded-b-2xl rounded-tr-2xl shadow-xl font-[poppins]">
                    <div className="flex justify-center items-center h-64 text-red-500">
                        <AlertCircle className="w-6 h-6 mr-2" />
                        <span>{error}</span>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="bg-white p-8 min-h-screen rounded-b-2xl rounded-tr-2xl shadow-xl font-[poppins]">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    {/* Notification */}
                    {notification && (
                        <div
                            className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-3 transition-all duration-300 ${notification.type === 'success' ? 'bg-green-500 text-white' :
                                    notification.type === 'error' ? 'bg-red-500 text-white' :
                                        'bg-blue-500 text-white'
                                }`}
                        >
                            {notification.type === 'success' ? <CheckCircle className="w-5 h-5" /> :
                                notification.type === 'error' ? <AlertCircle className="w-5 h-5" /> :
                                    <AlertCircle className="w-5 h-5" />}
                            <span className="font-[poppins]">{notification.message}</span>
                            <button onClick={() => setNotification(null)} className="ml-2 hover:opacity-80">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    )}

                    {/* Header */}
                    <div className="mb-8">
                        <button
                            onClick={goBack}
                            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-[#D67C2C] transition-colors mb-4"
                        >
                            <ArrowLeft className="w-5 h-5" />
                            Back to Assessments
                        </button>

                        <div className="text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6 bg-[#D67C2C]">
                                <Edit className="w-8 h-8 text-white" />
                            </div>
                            <h1 className="text-4xl font-bold text-gray-900 mb-4 font-[poppins]">
                                {existingMarks ? 'Edit Submission' : 'Submit Assessment'}
                            </h1>
                            <p className="text-xl text-gray-600 font-[poppins]">
                                {assessment?.setup?.title || 'Assessment'}
                            </p>
                        </div>
                    </div>

                    {/* Assessment Info */}
                    <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-6 mb-8">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="flex items-center gap-3">
                                <BookOpen className="w-6 h-6 text-blue-600" />
                                <div>
                                    <p className="text-sm text-gray-500">Subject</p>
                                    <p className="font-semibold text-gray-900">
                                        {assessment?.setup?.subject || assessment?.setup?.domain || 'N/A'}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <GraduationCap className="w-6 h-6 text-green-600" />
                                <div>
                                    <p className="text-sm text-gray-500">Grade</p>
                                    <p className="font-semibold text-gray-900">
                                        {assessment?.setup?.grade || 'N/A'}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <FileText className="w-6 h-6 text-purple-600" />
                                <div>
                                    <p className="text-sm text-gray-500">Total Questions</p>
                                    <p className="font-semibold text-gray-900">
                                        {assessment?.questions?.length || 0}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {existingMarks && (
                            <div className={`mt-6 p-4 rounded-lg border ${existingMarks.status === 'approved' ? 'bg-green-50 border-green-200' :
                                    existingMarks.status === 'revision_requested' ? 'bg-orange-50 border-orange-200' :
                                        existingMarks.status === 'submitted' ? 'bg-blue-50 border-blue-200' :
                                            'bg-gray-50 border-gray-200'
                                }`}>
                                <div className={`flex items-center gap-2 mb-2 ${existingMarks.status === 'approved' ? 'text-green-800' :
                                        existingMarks.status === 'revision_requested' ? 'text-orange-800' :
                                            existingMarks.status === 'submitted' ? 'text-blue-800' :
                                                'text-gray-800'
                                    }`}>
                                    {existingMarks.status === 'approved' ? <CheckCircle className="w-5 h-5" /> :
                                        existingMarks.status === 'revision_requested' ? <AlertCircle className="w-5 h-5" /> :
                                            existingMarks.status === 'submitted' ? <Clock className="w-5 h-5" /> :
                                                <AlertCircle className="w-5 h-5" />}
                                    <span className="font-medium">
                                        {existingMarks.status === 'approved' ? 'Approved' :
                                            existingMarks.status === 'revision_requested' ? 'Revision Requested' :
                                                existingMarks.status === 'submitted' ? 'Pending Review' :
                                                    'Submission Status'}
                                    </span>
                                </div>
                                <p className={`text-sm ${existingMarks.status === 'approved' ? 'text-green-700' :
                                        existingMarks.status === 'revision_requested' ? 'text-orange-700' :
                                            existingMarks.status === 'submitted' ? 'text-blue-700' :
                                                'text-gray-700'
                                    }`}>
                                    {existingMarks.status === 'approved' && 'Your submission has been approved by your teacher. You cannot edit it anymore.'}
                                    {existingMarks.status === 'revision_requested' && 'Your teacher has requested revisions. Please update your marks based on the feedback below.'}
                                    {existingMarks.status === 'submitted' && 'Your submission is pending teacher review.'}
                                </p>
                                {existingMarks.teacher_feedback && (
                                    <div className="mt-2">
                                        <p className={`text-sm font-medium ${existingMarks.status === 'approved' ? 'text-green-800' :
                                                existingMarks.status === 'revision_requested' ? 'text-orange-800' :
                                                    'text-blue-800'
                                            }`}>Teacher Feedback:</p>
                                        <p className={`text-sm italic ${existingMarks.status === 'approved' ? 'text-green-700' :
                                                existingMarks.status === 'revision_requested' ? 'text-orange-700' :
                                                    'text-blue-700'
                                            }`}>{existingMarks.teacher_feedback}</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Marks Entry Form */}
                    <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden mb-8">
                        <div className="p-6 border-b border-gray-100 bg-gray-50">
                            <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">Enter Your Marks</h2>
                            <p className="text-sm text-gray-600 mt-1">
                                Enter the marks you received for each question
                            </p>
                        </div>

                        <div className="p-6">
                            <div className="space-y-6">
                                {questionMarks.map((mark, index) => (
                                    <div key={mark.question_id} className="border border-gray-200 rounded-lg p-4">
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="flex-1">
                                                <h3 className="font-semibold text-gray-900 mb-2">
                                                    Question {mark.question_number}
                                                </h3>
                                                {assessment?.questions?.[index]?.question_text && (
                                                    <div
                                                        className="text-gray-700 mb-3"
                                                        dangerouslySetInnerHTML={{
                                                            __html: assessment.questions[index].question_text
                                                        }}
                                                    />
                                                )}
                                            </div>
                                            <div className="ml-4 text-right">
                                                <span className="text-sm font-medium text-gray-500">
                                                    Max: {mark.max_marks} marks
                                                </span>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-4">
                                            <label className="text-sm font-medium text-gray-700">
                                                Marks Obtained:
                                            </label>
                                            <input
                                                type="number"
                                                min="0"
                                                max={mark.max_marks}
                                                step="0.5"
                                                value={mark.obtained_marks || ''}
                                                onChange={(e) => handleMarkChange(index, e.target.value)}
                                                disabled={!canEdit()}
                                                className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent disabled:bg-gray-100"
                                                placeholder="0"
                                            />
                                            <span className="text-sm text-gray-500">
                                                / {mark.max_marks}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Self Feedback */}
                            <div className="mt-8">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Self Reflection (Optional)
                                </label>
                                <textarea
                                    value={selfFeedback}
                                    onChange={(e) => setSelfFeedback(e.target.value)}
                                    disabled={!canEdit()}
                                    rows={4}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent disabled:bg-gray-100"
                                    placeholder="Share your thoughts about this assessment, what you found challenging, what you learned, etc."
                                />
                            </div>
                        </div>
                    </div>

                    {/* Summary */}
                    <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-6 mb-8">
                        <h2 className="text-xl font-semibold text-gray-900 font-[poppins] mb-4">Summary</h2>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                            <div className="text-center">
                                <div className="text-2xl font-bold text-blue-600">
                                    {existingMarks?.total_marks || totalMarks}
                                </div>
                                <div className="text-sm text-gray-500">Total Marks</div>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-bold text-gray-600">
                                    {existingMarks?.max_total_marks || questionMarks.reduce((sum, mark) => sum + mark.max_marks, 0)}
                                </div>
                                <div className="text-sm text-gray-500">Maximum Marks</div>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-bold text-green-600">
                                    {existingMarks?.percentage || percentage}%
                                </div>
                                <div className="text-sm text-gray-500">Percentage</div>
                            </div>
                            <div className="text-center">
                                <div className={`text-2xl font-bold ${(existingMarks?.grade || grade) === 'A' ? 'text-green-600' :
                                        (existingMarks?.grade || grade) === 'B' ? 'text-blue-600' :
                                            (existingMarks?.grade || grade) === 'C' ? 'text-yellow-600' :
                                                (existingMarks?.grade || grade) === 'D' ? 'text-orange-600' : 'text-red-600'
                                    }`}>
                                    {existingMarks?.grade || grade}
                                </div>
                                <div className="text-sm text-gray-500">Grade</div>
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-4 justify-center">
                        {canEdit() && (
                            <button
                                onClick={handleSubmit}
                                disabled={saving}
                                className="flex items-center gap-2 px-6 py-3 bg-[#D67C2C] text-white rounded-lg hover:bg-[#c06a25] transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                            >
                                {saving ? (
                                    <Loader className="w-5 h-5 animate-spin" />
                                ) : (
                                    <Save className="w-5 h-5" />
                                )}
                                {existingMarks ? 'Update Submission' : 'Submit Marks'}
                            </button>
                        )}

                        <button
                            onClick={goBack}
                            className="flex items-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
                        >
                            <ArrowLeft className="w-5 h-5" />
                            Back to Assessments
                        </button>
                    </div>

                    {!canEdit() && (
                        <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200 text-center">
                            <p className="text-yellow-800 font-medium">
                                This submission has been approved and cannot be edited.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default StudentMarksEntry;